import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Sparkles, XCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LangContext";

export function PlanSuccess() {
  const { lang } = useLang();
  const isES = lang === "es";
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | ok | error
  const [attempts, setAttempts] = useState(0);
  const [trial, setTrial] = useState(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      setAttempts(tries);
      try {
        const { data } = await api.get(`/billing/session/${sessionId}`);
        if (data.status === "complete" || data.subscription) {
          if (!cancelled) { setTrial(!!data.trial); setStatus("ok"); }
          return;
        }
      } catch { /* ignore transient */ }
      if (tries < 8 && !cancelled) setTimeout(poll, 1500);
      else if (!cancelled) setStatus("ok"); // webhook may lag, but user completed checkout — trust it
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center p-6" data-testid="page-plan-success">
      <div className="max-w-md w-full sns-card p-8 md:p-10 text-center">
        {status === "checking" && (
          <>
            <div className="w-14 h-14 mx-auto mb-4 border-4 border-slate-200 border-t-[var(--coral)] rounded-full animate-spin" />
            <h1 className="sns-title text-2xl mb-2">
              {isES ? "Confirmando tu suscripción..." : "Confirming your subscription..."}
            </h1>
            <p className="text-slate-500 text-sm">
              {isES ? `Comprobando (${attempts}/8)...` : `Checking (${attempts}/8)...`}
            </p>
          </>
        )}
        {status === "ok" && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-4">
              <CheckCircle2 className="w-9 h-9" />
            </div>
            <div className="text-xs text-[var(--coral)] font-bold tracking-widest mb-2 inline-flex items-center gap-1">
              <Sparkles size={12} /> {isES ? "PLAN PRO ACTIVADO" : "PRO PLAN ACTIVE"}
            </div>
            <h1 className="sns-title text-3xl mb-3">
              {isES ? "¡Bienvenida a Pro!" : "Welcome to Pro!"}
            </h1>
            <p className="text-slate-600 mb-6">
              {trial
                ? (isES
                  ? "Tu prueba gratuita de 14 días acaba de empezar. Puedes cancelar en cualquier momento antes de que termine y no se te cobrará nada."
                  : "Your 14-day free trial just started. You can cancel anytime before it ends and you won't be charged.")
                : (isES
                  ? "Pago recibido. Tu equipo ya puede seguir trabajando con todas las funciones Pro. Recibirás la factura por email."
                  : "Payment received. Your team can keep working with every Pro feature. You'll get the invoice by email.")}
            </p>
            <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
              <p className="text-sm font-semibold text-slate-700 mb-2">
                {isES ? "Ya tienes acceso a:" : "You now have access to:"}
              </p>
              <ul className="space-y-1.5 text-sm text-slate-600">
                <li>✅ {isES ? "Hasta 50 propiedades y embarcaciones" : "Up to 50 properties and boats"}</li>
                <li>✅ {isES ? "50 usuarios · 30 empleados activos" : "50 users · 30 active employees"}</li>
                <li>✅ {isES ? "Mantenimientos sin límite" : "Unlimited maintenance"}</li>
                <li>✅ {isES ? "Integración Holded" : "Holded integration"}</li>
                <li>✅ {isES ? "Notificaciones push" : "Push notifications"}</li>
                <li>✅ {isES ? "Exportaciones e informes" : "Exports & reports"}</li>
              </ul>
            </div>
            <Link to="/" className="sns-btn sns-btn-accent w-full py-3 inline-flex items-center justify-center gap-2" data-testid="btn-go-app">
              {isES ? "Ir a la app" : "Go to the app"} <ArrowRight size={16} />
            </Link>
          </>
        )}
        {status === "error" && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 text-red-600 mb-4">
              <XCircle className="w-9 h-9" />
            </div>
            <h1 className="sns-title text-2xl mb-3">
              {isES ? "No hemos podido confirmar el pago" : "We couldn't confirm the payment"}
            </h1>
            <p className="text-slate-500 mb-6">
              {isES ? "Puedes intentar de nuevo desde la página del plan."
                    : "You can try again from the plan page."}
            </p>
            <Link to="/plan" className="sns-btn sns-btn-accent w-full py-3">
              {isES ? "Volver al plan" : "Back to plan"}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PlanCancel() {
  const { lang } = useLang();
  const isES = lang === "es";
  return (
    <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center p-6" data-testid="page-plan-cancel">
      <div className="max-w-md w-full sns-card p-8 md:p-10 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 text-slate-500 mb-4">
          <XCircle className="w-9 h-9" />
        </div>
        <h1 className="sns-title text-2xl mb-3">
          {isES ? "No pasa nada" : "No worries"}
        </h1>
        <p className="text-slate-500 mb-6">
          {isES
            ? "Has cancelado el checkout. No se te ha cobrado nada. Puedes volver cuando quieras a probar la app en modo Pro con 14 días gratis."
            : "You cancelled the checkout. Nothing was charged. Come back anytime to try the Pro plan with 14 days free."}
        </p>
        <div className="flex flex-col gap-2">
          <Link to="/plan" className="sns-btn sns-btn-accent w-full py-3" data-testid="btn-retry-plan">
            {isES ? "Volver al plan" : "Back to plan"}
          </Link>
          <Link to="/" className="sns-btn sns-btn-ghost bg-slate-100 w-full py-3">
            {isES ? "Ir a la app" : "Go to app"}
          </Link>
        </div>
      </div>
    </div>
  );
}
